import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import axios, {AxiosHeaders} from 'axios';

import {showErrorMessage} from '@/utils/showErrorMessage';

const axiosApiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

// Request interceptor for API calls
axiosApiInstance.interceptors.request.use(
  axiosRequestInterceptor,
  axiosRequestErrorInterceptor,
);

// Response interceptor for API calls
axiosApiInstance.interceptors.response.use(
  axiosResponseInterceptor,
  axiosResponseErrorInterceptor,
);

export default axiosApiInstance;

export function axiosRequestInterceptor(config: InternalAxiosRequestConfig) {
  const accessToken = localStorage.getItem('afriex-token');
  // `AxiosHeaders.from` copies the existing headers through the public API;
  // spreading the instance only worked because AxiosHeaders happens to keep
  // header values as own properties.
  const headers = AxiosHeaders.from(config.headers);
  config.headers = headers.set({
    // A caller's own token wins (the password reset sends its OTP's token
    // before that token becomes the session).
    Authorization: headers.get('Authorization') ?? `Bearer ${accessToken}`,
    'afriex-client': 'admin',
    // The build, so the server can tell requests from a stale tab apart from
    // the current deploy in its traces.
    'x-client-version': __APP_VERSION__,
    'x-require-token-audience': 'true',
  });
  return config;
}

export function axiosRequestErrorInterceptor(error: AxiosError) {
  return Promise.reject(error);
}

export function axiosResponseInterceptor(response: AxiosResponse) {
  return response;
}

// A 403 is left to the screen, which shows the server's reason (see
// utils/errorMessage.ts); a generic toast here only duplicated it. A 401 still
// signs the admin out here, since no screen can recover from it.
export async function axiosResponseErrorInterceptor(error: AxiosError) {
  if (error.response?.status === 401) {
    showErrorMessage('Your login session has expired. Please login again');
    localStorage.clear();
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
  }
  return Promise.reject(error);
}
